import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, ArrowRight } from "lucide-react";

export default function Bulk() {
  const [tiers, setTiers] = useState([]);
  const { cart, updateQty, removeFromCart, clearCart } = useApp();
  const navigate = useNavigate();

  useEffect(() => { api.get("/wholesale/tiers").then(({ data }) => setTiers(data)).catch(() => {}); }, []);

  const subtotal = cart.reduce((a, x) => a + x.variant.price * x.qty, 0);
  const totalQty = cart.reduce((a, x) => a + x.qty, 0);
  const sorted = tiers.slice().sort((a, b) => b.min_qty - a.min_qty);
  const tier = sorted.find((t) => totalQty >= t.min_qty) || { discount_pct: 0, name: "Standard" };
  const next = tiers.slice().sort((a, b) => a.min_qty - b.min_qty).find((t) => totalQty < t.min_qty);
  const discount = subtotal * tier.discount_pct / 100;
  const total = subtotal - discount;
  const totalMg = cart.reduce((a, x) => a + x.variant.mg * x.qty, 0);
  const pricePerMg = totalMg > 0 ? total / totalMg : 0;

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Bulk buying engine</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-4">Build a Bulk Lab Order</h1>
      <p className="text-slate-400 text-sm mb-10">Mix SKUs. Reach the next pricing tier. Watch savings compute in real time.</p>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-4">
          {cart.length === 0 && (
            <div className="glass rounded-2xl p-12 text-center text-slate-500 text-sm">
              Your basket is empty. <Link to="/shop" className="text-cyan-300">Add products from the shop</Link>.
            </div>
          )}
          {cart.map((x) => (
            <div key={x.variant.id} className="glass rounded-2xl p-4 flex items-center gap-4" data-testid={`bulk-row-${x.variant.sku}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{x.product.name}</div>
                <div className="text-[11px] font-mono text-slate-500">{x.variant.sku} · ${x.variant.price_per_mg}/mg</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(x.variant.id, x.qty - 1)} className="w-7 h-7 rounded-lg glass text-sm">−</button>
                <span className="w-8 text-center font-mono text-sm">{x.qty}</span>
                <button onClick={() => updateQty(x.variant.id, x.qty + 1)} className="w-7 h-7 rounded-lg glass text-sm">+</button>
              </div>
              <div className="w-24 text-right font-mono text-sm text-cyan-300">${(x.variant.price * x.qty).toFixed(2)}</div>
              <button onClick={() => removeFromCart(x.variant.id)} className="text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-5 space-y-4">
          <div className="glass rounded-2xl p-6">
            <div className="mono-label mb-4">Volume tiers</div>
            <div className="space-y-2">
              {tiers.slice().sort((a, b) => a.min_qty - b.min_qty).map((t) => (
                <div key={t.name} data-testid={`wholesale-tier-row-${t.name.toLowerCase().replace(" ", "-")}`}
                  className={`flex items-center justify-between rounded-xl border p-3 text-sm font-mono ${tier.name === t.name ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/5 text-slate-400"}`}>
                  <span>{t.name}</span>
                  <span>{t.min_qty}–{t.max_qty === 9999 ? "∞" : t.max_qty} units</span>
                  <span className="text-cyan-300">{t.discount_pct}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-strong rounded-2xl p-6 space-y-2" data-testid="bulk-summary">
            {next && totalQty > 0 && (
              <div className="rounded-xl border border-violet-400/30 bg-violet-500/10 p-3 text-xs font-mono text-violet-300">
                YOU ARE {next.min_qty - totalQty} VIAL{next.min_qty - totalQty > 1 ? "S" : ""} AWAY FROM THE {next.discount_pct}% TIER.
              </div>
            )}
            <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-400">Bulk tier</span><span className="font-mono text-cyan-300">{tier.name} · {tier.discount_pct}%</span></div>
            <div className="flex justify-between text-sm text-emerald-400"><span>Savings</span><span className="font-mono">−${discount.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-400">Avg price/mg</span><span className="font-mono">${pricePerMg.toFixed(2)}</span></div>
            <div className="laser-line my-2" />
            <div className="flex justify-between text-lg"><span>Total</span><span className="font-mono text-cyan-300">${total.toFixed(2)}</span></div>
            <Button onClick={() => cart.length ? navigate("/checkout") : toast.error("Basket empty")} data-testid="bulk-checkout-btn"
              className="w-full mt-3 btn-primary rounded-xl py-3 text-sm tracking-wide">
              Checkout Bulk Order <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
